import {
  STREAMLAIN_PANEL_LAYER_ENTER,
  STREAMLAIN_PANEL_LAYER_EXIT,
  type StreamLainAnimationController,
  streamLainAnimationController,
  type StreamLainAnimationHandle,
} from '@blocksuite/affine-shared/utils';
import { css } from '@emotion/css';
import { html, nothing, render, type TemplateResult } from 'lit';

import { dataViewI18nT } from '../../../../core/i18n.js';

export const CALENDAR_DETAIL_SETTLED_EVENT =
  'streamlain:calendar-detail-settled';
export const CALENDAR_DETAIL_DEFAULT_WIDTH = 340;
export const CALENDAR_DETAIL_MIN_WIDTH = 280;
export const CALENDAR_DETAIL_MAX_WIDTH = 520;
export interface CalendarDetailShellState {
  readonly contentKey: string | null;
  readonly content: TemplateResult | typeof nothing;
  readonly width: number;
  readonly overlay: boolean;
  readonly returnFocus: HTMLElement | null;
}
const shellClass = css({
  background: 'var(--streamlain-v2-layer-background-primary)',
  borderLeft: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  boxSizing: 'border-box',
  display: 'flex',
  flex: '0 0 auto',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  '&[hidden]': { display: 'none' },
  '&[data-calendar-detail-overlay="true"]': {
    position: 'absolute',
    inset: 0,
    zIndex: 20,
  },
  '& :is([data-calendar-detail-shell-layer], [data-calendar-detail-leaving])': {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    height: '100%',
  },
  '& :is([data-calendar-detail-shell-layer], [data-calendar-detail-leaving]) > *':
    {
      flex: '1 1 auto',
      minWidth: 0,
      minHeight: 0,
      width: '100%',
    },
  '& :is([data-calendar-detail-shell-layer], [data-calendar-detail-leaving]) aside':
    {
      border: 'none',
      boxShadow: 'none',
    },
  '& [data-calendar-detail-resize]': {
    position: 'absolute',
    inset: '0 auto 0 0',
    width: 8,
    cursor: 'col-resize',
    touchAction: 'none',
    zIndex: 1,
  },
  '& [data-calendar-detail-resize]:focus-visible': {
    outline: '2px solid var(--streamlain-v2-brand-primary)',
    outlineOffset: -2,
  },
});
const clamp = (width: number) =>
  Math.min(
    CALENDAR_DETAIL_MAX_WIDTH,
    Math.max(CALENDAR_DETAIL_MIN_WIDTH, Math.round(width))
  );

/** One shell lifetime for a native form or the Planner's existing source-view embed. */
export class CalendarDetailShellController {
  private root: HTMLElement | undefined;
  private state: CalendarDetailShellState | undefined;
  private key: string | null = null;
  private layer: HTMLElement | undefined;
  private width = CALENDAR_DETAIL_DEFAULT_WIDTH;
  private requestedWidth: number | undefined;
  private overlay = false;
  private revision = 0;
  private widthMotion: StreamLainAnimationHandle | undefined;
  private readonly leaving = new Set<HTMLElement>();
  private returnFocus: HTMLElement | null = null;
  private observer: ResizeObserver | undefined;
  private focusObserver: MutationObserver | undefined;
  private stopPointer: (() => void) | undefined;
  private readonly background = new Map<HTMLElement, boolean>();
  private readonly onKey = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.isComposing || this.root?.inert) return;
    if (event.key === 'Escape' && !event.repeat) {
      const close = this.root?.querySelector<HTMLButtonElement>(
        '[data-calendar-detail-close]'
      );
      if (close) {
        event.preventDefault();
        event.stopPropagation();
        close.click();
      }
    } else if (event.key === 'Tab' && this.overlay && this.root) {
      const items = [
        ...this.root.querySelectorAll<HTMLElement>(
          'button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),a[href],[tabindex="0"]'
        ),
      ].filter(item => !item.hidden && !item.closest('[hidden], [inert]'));
      const first = items[0];
      const last = items.at(-1);
      const active = this.root.ownerDocument.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };
  constructor(
    private readonly controller: StreamLainAnimationController = streamLainAnimationController
  ) {}
  update(root: HTMLElement, state: CalendarDetailShellState): void {
    if (this.root && this.root !== root) this.dispose();
    if (!this.root) {
      this.root = root;
      root.classList.add(shellClass);
      root.dataset.calendarDetailShell = 'true';
      root.tabIndex = -1;
      root.hidden = true;
      root.addEventListener('keydown', this.onKey);
      this.observer = new ResizeObserver(() => this.syncGeometry());
      if (root.parentElement) this.observer.observe(root.parentElement);
    }
    const currentWidth = root.hidden
      ? 0
      : Number.parseFloat(root.style.width) || this.width;
    this.state = state;
    if (this.requestedWidth !== state.width) {
      this.requestedWidth = state.width;
      this.width = clamp(state.width);
    }
    this.syncGeometry();
    const nextKey = state.content !== nothing ? state.contentKey : null;
    if (nextKey && state.returnFocus) this.returnFocus = state.returnFocus;
    if (!nextKey) {
      if (!this.key) return;
      this.key = null;
      root.inert = true;
      this.focusObserver?.disconnect();
      this.stopPointer?.();
      const revision = ++this.revision;
      const layer = root.querySelector<HTMLElement>(
        '[data-calendar-detail-shell-layer]'
      )!;
      this.controller.play(layer, STREAMLAIN_PANEL_LAYER_EXIT, 'exit');
      const from = currentWidth;
      if (!this.overlay) root.style.width = `${from}px`;
      const exit = (this.widthMotion = this.overlay
        ? this.controller.play(root, STREAMLAIN_PANEL_LAYER_EXIT, 'exit')
        : this.controller.animateProgress(
            root,
            progress => {
              root.style.width = `${from * (1 - progress)}px`;
            },
            'exit'
          ));
      void exit.finished.then(outcome => {
        if (outcome !== 'finished' || revision !== this.revision) return;
        this.widthMotion = undefined;
        root.hidden = true;
        this.clearLeaving();
        if (this.layer) {
          this.controller.cancel(this.layer);
          render(nothing, this.layer);
        }
        this.layer = undefined;
        render(nothing, root);
        root.dispatchEvent(new CustomEvent(CALENDAR_DETAIL_SETTLED_EVENT));
        this.restoreBackground();
        if (this.returnFocus?.isConnected)
          this.returnFocus.focus({ preventScroll: true });
      });
      return;
    }
    const opening = !this.key;
    const changed = this.key !== nextKey;
    let leaving: HTMLElement | undefined;
    if (changed && this.layer) {
      leaving = this.layer;
      this.clearLeaving();
      leaving.removeAttribute('data-calendar-detail-shell-layer');
      leaving.dataset.calendarDetailLeaving = 'true';
      leaving.inert = true;
      leaving.setAttribute('aria-hidden', 'true');
      Object.assign(leaving.style, {
        position: 'absolute',
        inset: '0',
        width: this.overlay ? '100%' : `${this.width}px`,
        pointerEvents: 'none',
      });
    }
    this.key = nextKey;
    root.hidden = false;
    root.inert = false;
    let container = root.querySelector<HTMLElement>(
      '[data-calendar-detail-shell-layers]'
    );
    if (!container) {
      render(
        html`<div
            data-calendar-detail-resize
            role="separator"
            tabindex="0"
            aria-label=${dataViewI18nT('calendar.accessibility.resizeDetails')}
            aria-orientation="vertical"
            aria-valuemin=${CALENDAR_DETAIL_MIN_WIDTH}
            aria-valuemax=${CALENDAR_DETAIL_MAX_WIDTH}
            aria-valuenow=${this.width}
            ?hidden=${this.overlay}
            @keydown=${this.resizeKey}
            @pointerdown=${this.resizePointer}
          ></div>
          <div
            data-calendar-detail-shell-layers
            style="display:contents"
          ></div>`,
        root
      );
      container = root.querySelector<HTMLElement>(
        '[data-calendar-detail-shell-layers]'
      )!;
    }
    if (changed || !this.layer) {
      this.layer = root.ownerDocument.createElement('div');
      this.layer.dataset.calendarDetailShellLayer = 'true';
      container.insertBefore(this.layer, leaving ?? null);
    }
    render(state.content, this.layer);
    this.syncGeometry();
    if (changed) {
      ++this.revision;
      const layer = root.querySelector<HTMLElement>(
        '[data-calendar-detail-shell-layer]'
      )!;
      layer.style.minWidth = this.overlay ? '0' : `${this.width}px`;
      layer.style.width = this.overlay ? '100%' : `${this.width}px`;
      this.controller.play(layer, STREAMLAIN_PANEL_LAYER_ENTER, 'enter');
      if (leaving) {
        const old = leaving;
        this.leaving.add(old);
        const exit = this.controller.play(
          old,
          STREAMLAIN_PANEL_LAYER_EXIT,
          'exit'
        );
        void exit.finished.then(() => {
          render(nothing, old);
          old.remove();
          this.leaving.delete(old);
          if (this.key && !this.leaving.size)
            root.dispatchEvent(new CustomEvent(CALENDAR_DETAIL_SETTLED_EVENT));
        });
      }
      if (opening && this.overlay) {
        const handle = (this.widthMotion = this.controller.play(
          root,
          STREAMLAIN_PANEL_LAYER_ENTER,
          'enter'
        ));
        void handle.finished.then(() => {
          if (this.widthMotion === handle) {
            this.widthMotion = undefined;
            this.syncGeometry();
          }
        });
      }
      if (opening && !this.overlay) {
        const from = currentWidth;
        root.style.width = `${from}px`;
        const handle = (this.widthMotion = this.controller.animateProgress(
          root,
          progress => {
            root.style.width = `${from + (this.width - from) * progress}px`;
          }
        ));
        void handle.finished.then(() => {
          if (this.widthMotion === handle) {
            this.widthMotion = undefined;
            this.syncGeometry();
          }
        });
      }
      this.focusContent();
    }
  }
  resize(width: number): void {
    this.stopSizeMotion();
    this.width = clamp(width);
    this.syncGeometry();
  }
  private stopSizeMotion(): void {
    if (this.root) this.controller.cancel(this.root);
    this.widthMotion = undefined;
  }
  private clearLeaving(): void {
    for (const layer of this.leaving) {
      this.controller.cancel(layer);
      render(nothing, layer);
      layer.remove();
    }
    this.leaving.clear();
  }
  private syncGeometry(): void {
    const root = this.root;
    if (!root || !this.state || (!this.key && this.widthMotion)) return;
    const previousOverlay = this.overlay;
    const hostWidth = root.parentElement?.getBoundingClientRect().width;
    this.overlay =
      this.state.overlay ||
      (hostWidth !== undefined && hostWidth > 0 && hostWidth <= 820);
    if (previousOverlay !== this.overlay) this.stopSizeMotion();
    root.dataset.calendarDetailOverlay = String(this.overlay);
    root.setAttribute('role', this.overlay ? 'dialog' : 'complementary');
    root.setAttribute('aria-label', dataViewI18nT('calendar.detail.event'));
    if (this.overlay) root.setAttribute('aria-modal', 'true');
    else root.removeAttribute('aria-modal');
    if (this.key && !this.widthMotion)
      root.style.width = this.overlay ? '100%' : `${this.width}px`;
    const layer = root.querySelector<HTMLElement>(
      '[data-calendar-detail-shell-layer]'
    );
    if (layer && this.key) {
      layer.style.minWidth = this.overlay ? '0' : `${this.width}px`;
      layer.style.width = this.overlay ? '100%' : `${this.width}px`;
    }
    const separator = root.querySelector<HTMLElement>(
      '[data-calendar-detail-resize]'
    );
    if (separator) {
      separator.hidden = this.overlay;
      separator.setAttribute('aria-valuenow', String(this.width));
    }
    if (this.overlay && this.key) {
      for (const sibling of root.parentElement?.children ?? [])
        if (
          sibling instanceof HTMLElement &&
          sibling !== root &&
          !this.background.has(sibling)
        ) {
          this.background.set(sibling, sibling.inert);
          sibling.inert = true;
        }
    } else this.restoreBackground();
  }
  private restoreBackground(): void {
    for (const [element, inert] of this.background) element.inert = inert;
    this.background.clear();
  }
  private focusContent(): void {
    this.focusObserver?.disconnect();
    const root = this.root!;
    const focus = () => {
      const target = [
        ...root.querySelectorAll<HTMLElement>(
          'input:not(:disabled),textarea:not(:disabled),[data-calendar-detail-close],button:not(:disabled)'
        ),
      ].find(
        element => !element.closest('[inert],[aria-hidden="true"],[hidden]')
      );
      if (!target || root.inert) return false;
      target.focus({ preventScroll: true });
      this.focusObserver?.disconnect();
      return true;
    };
    if (!focus()) {
      root.focus({ preventScroll: true });
      this.focusObserver = new MutationObserver(focus);
      this.focusObserver.observe(root, { childList: true, subtree: true });
    }
  }
  private readonly resizeKey = (event: KeyboardEvent) => {
    const step = event.shiftKey ? 40 : 16;
    const width =
      event.key === 'ArrowLeft'
        ? this.width + step
        : event.key === 'ArrowRight'
          ? this.width - step
          : event.key === 'Home'
            ? CALENDAR_DETAIL_MIN_WIDTH
            : event.key === 'End'
              ? CALENDAR_DETAIL_MAX_WIDTH
              : null;
    if (width !== null) {
      event.preventDefault();
      event.stopPropagation();
      this.resize(width);
    }
  };
  private readonly resizePointer = (event: PointerEvent) => {
    if (event.button !== 0 || this.overlay) return;
    event.preventDefault();
    event.stopPropagation();
    this.stopPointer?.();
    this.stopSizeMotion();
    const start = event.clientX;
    const width = this.width;
    const doc = this.root!.ownerDocument;
    const view = doc.defaultView!;
    const cursor = doc.body.style.cursor;
    const userSelect = doc.body.style.userSelect;
    doc.body.style.cursor = 'col-resize';
    doc.body.style.userSelect = 'none';
    const move = (event: PointerEvent) => {
      event.preventDefault();
      this.resize(width - (event.clientX - start));
    };
    const stop = () => {
      view.removeEventListener('pointermove', move);
      view.removeEventListener('pointerup', stop);
      view.removeEventListener('pointercancel', stop);
      doc.body.style.cursor = cursor;
      doc.body.style.userSelect = userSelect;
      this.stopPointer = undefined;
    };
    view.addEventListener('pointermove', move, { passive: false });
    view.addEventListener('pointerup', stop);
    view.addEventListener('pointercancel', stop);
    this.stopPointer = stop;
  };
  dispose(): void {
    ++this.revision;
    this.clearLeaving();
    this.widthMotion = undefined;
    this.stopPointer?.();
    this.observer?.disconnect();
    this.focusObserver?.disconnect();
    this.restoreBackground();
    if (this.root) {
      this.controller.cancel(this.root);
      const layer = this.root.querySelector<HTMLElement>(
        '[data-calendar-detail-shell-layer]'
      );
      if (layer) {
        this.controller.cancel(layer);
        render(nothing, layer);
      }
      this.root.removeEventListener('keydown', this.onKey);
      render(nothing, this.root);
      this.root.hidden = true;
    }
    this.root = undefined;
    this.state = undefined;
    this.key = null;
    this.layer = undefined;
  }
}
